function boxBlur1D(src, dst, size, stride, count, radius) {
    if (radius <= 0) {
        dst.set(src);
        return;
    }
    const windowSize = radius * 2 + 1;
    for (let line = 0; line < count; line++) {
        const base = line * stride;
        let sum = 0;
        for (let i = -radius; i <= radius; i++) {
            const idx = Math.min(Math.max(i, 0), size - 1);
            sum += src[base + idx];
        }
        for (let i = 0; i < size; i++) {
            dst[base + i] = sum / windowSize;
            const addIdx = Math.min(Math.max(i + radius + 1, 0), size - 1);
            const subIdx = Math.min(Math.max(i - radius, 0), size - 1);
            sum += src[base + addIdx] - src[base + subIdx];
        }
    }
}
/** Three-pass box blur approximates a Gaussian blur closely while staying cheap. */
function gaussianApproxBlur(alpha, width, height, radiusPx) {
    if (radiusPx <= 0)
        return Uint8ClampedArray.from(alpha);
    const radius = Math.max(1, Math.round(radiusPx));
    let buf = new Float32Array(alpha.length);
    for (let i = 0; i < alpha.length; i++)
        buf[i] = alpha[i];
    let tmp = new Float32Array(alpha.length);
    for (let pass = 0; pass < 3; pass++) {
        // Horizontal: rows are contiguous.
        boxBlur1D(buf, tmp, width, width, height, radius);
        [buf, tmp] = [tmp, buf];
        // Vertical: transpose-free pass by treating columns with stride `width`.
        const vSrc = buf;
        const vDst = tmp;
        for (let x = 0; x < width; x++) {
            let sum = 0;
            const windowSize = radius * 2 + 1;
            for (let i = -radius; i <= radius; i++) {
                const idx = Math.min(Math.max(i, 0), height - 1);
                sum += vSrc[idx * width + x];
            }
            for (let y = 0; y < height; y++) {
                vDst[y * width + x] = sum / windowSize;
                const addIdx = Math.min(Math.max(y + radius + 1, 0), height - 1);
                const subIdx = Math.min(Math.max(y - radius, 0), height - 1);
                sum += vSrc[addIdx * width + x] - vSrc[subIdx * width + x];
            }
        }
        [buf, tmp] = [tmp, buf];
    }
    const out = new Uint8ClampedArray(alpha.length);
    for (let i = 0; i < out.length; i++)
        out[i] = Math.round(buf[i]);
    return out;
}
/** Feathers (softens) the mask edge. radiusPx: 0-10. */
export function featherMask(mask, radiusPx) {
    const clamped = Math.min(Math.max(radiusPx, 0), 10);
    return { width: mask.width, height: mask.height, alpha: gaussianApproxBlur(mask.alpha, mask.width, mask.height, clamped) };
}
function separableMinMax(alpha, width, height, radius, mode) {
    if (radius === 0)
        return Uint8ClampedArray.from(alpha);
    const pick = mode === 'min' ? Math.min : Math.max;
    const h = new Uint8ClampedArray(alpha.length);
    for (let y = 0; y < height; y++) {
        const rowBase = y * width;
        for (let x = 0; x < width; x++) {
            let acc = mode === 'min' ? 255 : 0;
            const lo = Math.max(0, x - radius);
            const hi = Math.min(width - 1, x + radius);
            for (let i = lo; i <= hi; i++)
                acc = pick(acc, alpha[rowBase + i]);
            h[rowBase + x] = acc;
        }
    }
    const out = new Uint8ClampedArray(alpha.length);
    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            let acc = mode === 'min' ? 255 : 0;
            const lo = Math.max(0, y - radius);
            const hi = Math.min(height - 1, y + radius);
            for (let i = lo; i <= hi; i++)
                acc = pick(acc, h[i * width + x]);
            out[y * width + x] = acc;
        }
    }
    return out;
}
/**
 * Chokes (shrinks, negative px) or expands (grows, positive px) the mask edge
 * using a square structuring element morphological erode/dilate.
 */
export function chokeExpandMask(mask, px) {
    const radius = Math.round(Math.abs(px));
    if (radius === 0)
        return { width: mask.width, height: mask.height, alpha: Uint8ClampedArray.from(mask.alpha) };
    const mode = px < 0 ? 'min' : 'max'; // choke = erode (min), expand = dilate (max)
    return { width: mask.width, height: mask.height, alpha: separableMinMax(mask.alpha, mask.width, mask.height, radius, mode) };
}
/**
 * Removes background-colored fringe/halo from semi-transparent edge pixels by solving for the
 * true foreground color: observed = fg*alpha + bg*(1-alpha)  =>  fg = (observed - bg*(1-alpha)) / alpha.
 * Only affects pixels with 0 < alpha < 255; fully opaque/transparent pixels are left untouched.
 */
export function decontaminateEdges(rgba, mask, bg) {
    const out = Uint8ClampedArray.from(rgba);
    const pixelCount = mask.width * mask.height;
    for (let i = 0; i < pixelCount; i++) {
        const a = mask.alpha[i];
        if (a === 0 || a === 255)
            continue;
        const alphaFrac = a / 255;
        const o = i * 4;
        const invA = 1 - alphaFrac;
        const r = (out[o] - bg.r * invA) / alphaFrac;
        const g = (out[o + 1] - bg.g * invA) / alphaFrac;
        const b = (out[o + 2] - bg.b * invA) / alphaFrac;
        out[o] = Math.min(255, Math.max(0, Math.round(r)));
        out[o + 1] = Math.min(255, Math.max(0, Math.round(g)));
        out[o + 2] = Math.min(255, Math.max(0, Math.round(b)));
    }
    return out;
}
/**
 * Like {@link decontaminateEdges}, but with no single global background colour: for every
 * semi-transparent edge pixel the background is estimated from the nearby *fully removed*
 * pixels and the foreground from the nearby *fully kept* pixels. This handles gradients,
 * multi-coloured and busy backgrounds, where a global colour would tint the fringe wrong.
 * Where the alpha is tiny the un-mixing divide amplifies noise, so the result is blended
 * toward the local foreground estimate in proportion to how transparent the pixel is.
 */
export function decontaminateEdgesLocal(rgba, mask, options = {}) {
    const { width, height } = mask;
    const out = Uint8ClampedArray.from(rgba);
    const workingLongSide = options.workingLongSide ?? 768;
    const factor = Math.max(1, Math.ceil(Math.max(width, height) / workingLongSide));
    const w = Math.max(1, Math.ceil(width / factor));
    const h = Math.max(1, Math.ceil(height / factor));
    const radius = options.radius ?? Math.max(3, Math.round(Math.max(w, h) / 80));
    // Accumulate colour sums of sure-background / sure-foreground pixels per working cell.
    const n = w * h;
    const bgSum = [new Float32Array(n), new Float32Array(n), new Float32Array(n)];
    const bgCnt = new Float32Array(n);
    const fgSum = [new Float32Array(n), new Float32Array(n), new Float32Array(n)];
    const fgCnt = new Float32Array(n);
    for (let y = 0; y < height; y++) {
        const cy = (y / factor) | 0;
        for (let x = 0; x < width; x++) {
            const i = y * width + x;
            const a = mask.alpha[i];
            if (a > 8 && a < 247)
                continue;
            const ci = cy * w + ((x / factor) | 0);
            const o = i * 4;
            const sums = a <= 8 ? bgSum : fgSum;
            sums[0][ci] += rgba[o];
            sums[1][ci] += rgba[o + 1];
            sums[2][ci] += rgba[o + 2];
            if (a <= 8)
                bgCnt[ci] += 1;
            else
                fgCnt[ci] += 1;
        }
    }
    // Box-average the sums and counts; the ratio is a count-weighted local mean colour.
    const box = (src) => boxSum(src, w, h, radius);
    const bgR = box(bgSum[0]);
    const bgG = box(bgSum[1]);
    const bgB = box(bgSum[2]);
    const bgN = box(bgCnt);
    const fgR = box(fgSum[0]);
    const fgG = box(fgSum[1]);
    const fgB = box(fgSum[2]);
    const fgN = box(fgCnt);
    for (let y = 0; y < height; y++) {
        const cy = (y / factor) | 0;
        for (let x = 0; x < width; x++) {
            const i = y * width + x;
            const a = mask.alpha[i];
            if (a <= 8 || a >= 247)
                continue;
            const ci = cy * w + ((x / factor) | 0);
            const nb = bgN[ci];
            if (nb <= 0)
                continue; // no background nearby to un-mix against
            const bgr = bgR[ci] / nb;
            const bgg = bgG[ci] / nb;
            const bgb = bgB[ci] / nb;
            const alphaFrac = a / 255;
            const invA = 1 - alphaFrac;
            const o = i * 4;
            let r = (out[o] - bgr * invA) / alphaFrac;
            let g = (out[o + 1] - bgg * invA) / alphaFrac;
            let b = (out[o + 2] - bgb * invA) / alphaFrac;
            const nf = fgN[ci];
            if (nf > 0) {
                // Trust the solved colour in proportion to alpha; lean on the local foreground mean otherwise.
                const t = alphaFrac;
                r = r * t + (fgR[ci] / nf) * (1 - t);
                g = g * t + (fgG[ci] / nf) * (1 - t);
                b = b * t + (fgB[ci] / nf) * (1 - t);
            }
            out[o] = Math.min(255, Math.max(0, Math.round(r)));
            out[o + 1] = Math.min(255, Math.max(0, Math.round(g)));
            out[o + 2] = Math.min(255, Math.max(0, Math.round(b)));
        }
    }
    return out;
}
/** Summed-area-table box *sum* over a (2r+1)^2 edge-clamped window. */
function boxSum(src, width, height, radius) {
    const out = new Float32Array(width * height);
    const satW = width + 1;
    const sat = new Float64Array(satW * (height + 1));
    for (let y = 0; y < height; y++) {
        let rowSum = 0;
        for (let x = 0; x < width; x++) {
            rowSum += src[y * width + x];
            sat[(y + 1) * satW + x + 1] = sat[y * satW + x + 1] + rowSum;
        }
    }
    for (let y = 0; y < height; y++) {
        const y0 = Math.max(0, y - radius);
        const y1 = Math.min(height - 1, y + radius) + 1;
        for (let x = 0; x < width; x++) {
            const x0 = Math.max(0, x - radius);
            const x1 = Math.min(width - 1, x + radius) + 1;
            out[y * width + x] = sat[y1 * satW + x1] - sat[y1 * satW + x0] - sat[y0 * satW + x1] + sat[y0 * satW + x0];
        }
    }
    return out;
}
/** Applies a mask's alpha channel onto an RGBA buffer (does not copy RGB). */
export function applyMaskAlpha(rgba, mask) {
    const out = Uint8ClampedArray.from(rgba);
    const pixelCount = mask.width * mask.height;
    for (let i = 0; i < pixelCount; i++) {
        out[i * 4 + 3] = mask.alpha[i];
    }
    return out;
}
//# sourceMappingURL=maskOps.js.map